import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Check, Users, BookOpen, Building2, ShoppingCart } from 'lucide-react';
import {
  getPublicPlans,
  createCheckoutSession,
  getMyTenantSubscription,
  validateCouponCode,
  type BillingPlan,
  type TenantSubscription,
} from '../../modules/billing/api/billing.client';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';

function formatPrice(amount: number, currency: string, interval: string): string {
  const value = (amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const curr = currency?.toUpperCase() || 'USD';
  const intervalLabel = interval === 'year' ? '/year' : '/month';
  return `${curr} ${value}${intervalLabel}`;
}

const normalizeCoupon = (v: string) => v.trim().toUpperCase();

type CouponStatus = 'idle' | 'applied' | 'invalid' | 'checking';

interface PublicPlanCardProps {
  plan: BillingPlan;
  isCurrentPlan: boolean;
  onOpenCheckoutModal: (planId: string) => void;
}

function PublicPlanCard({ plan, isCurrentPlan, onOpenCheckoutModal }: PublicPlanCardProps) {
  const limits = plan.limits ?? {};
  const marketingFeatures = plan.marketing_features ?? [];
  const isActive = plan.status === 'active';
  const buttonLabel = isCurrentPlan ? 'Your current plan' : 'Subscribe';

  return (
    <Card className="h-full flex flex-col" padding="none" hover>
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
          <h3 className="text-xl font-semibold min-w-0 sm:pr-2 text-app-text dark:text-white leading-tight">
            <span className="block break-words">{plan.name}</span>
          </h3>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {isCurrentPlan && (
              <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-emerald-600/20 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-200 dark:ring-emerald-500/30">
                Current plan
              </span>
            )}
            <Badge variant={isActive ? 'success' : 'gray'}>
              {plan.status}
            </Badge>
          </div>
        </div>
        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-3">
          {formatPrice(plan.amount, plan.currency, plan.interval)}
        </p>
        {plan.description && (
          <p className="text-sm text-app-text-muted dark:text-gray-400 mb-4 line-clamp-3">
            {plan.description}
          </p>
        )}
        {marketingFeatures.length > 0 && (
          <ul className="space-y-2 mb-4 flex-1">
            {marketingFeatures.slice(0, 5).map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-app-text dark:text-gray-200">
                <Check className="w-4 h-4 shrink-0 text-primary-500 mt-0.5" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-4 border-t border-app-border dark:border-gray-600 space-y-2">
          {limits.max_students != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <Users className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_students} students</span>
            </div>
          )}
          {limits.max_teachers != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <BookOpen className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_teachers} teachers</span>
            </div>
          )}
          {limits.max_schools != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <Building2 className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_schools} schools</span>
            </div>
          )}
          <div className="pt-3">
            <Button
              type="button"
              onClick={() => !isCurrentPlan && onOpenCheckoutModal(plan.plan_id)}
              disabled={isCurrentPlan}
              className="w-full"
            >
              {!isCurrentPlan && <ShoppingCart className="w-4 h-4 mr-2" aria-hidden />}
              {buttonLabel}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export const BillingPlansPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [sub, setSub] = useState<TenantSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const checkoutMessageShown = useRef(false);

  const [checkoutModalPlanId, setCheckoutModalPlanId] = useState<string | null>(null);
  const [couponByPlan, setCouponByPlan] = useState<Record<string, string>>({});
  const [couponStatusByPlan, setCouponStatusByPlan] = useState<Record<string, CouponStatus>>({});
  const [couponMsgByPlan, setCouponMsgByPlan] = useState<Record<string, string>>({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleApplyCoupon = async (planId: string) => {
    const raw = couponByPlan[planId] ?? '';
    const code = normalizeCoupon(raw);
    if (!code) return;

    setCouponStatusByPlan((m) => ({ ...m, [planId]: 'checking' }));
    setCouponMsgByPlan((m) => ({ ...m, [planId]: 'Checking coupon...' }));

    try {
      const res = await validateCouponCode(code);

      if (res.valid) {
        setCouponStatusByPlan((m) => ({ ...m, [planId]: 'applied' }));
        const preview =
          res.type === 'percent'
            ? `${res.value}% off`
            : `${(res.value / 100).toFixed(2)} ${String(res.currency ?? 'USD').toUpperCase()} off`;
        setCouponMsgByPlan((m) => ({
          ...m,
          [planId]: `Applied ✓ ${code} (${preview})`,
        }));
      } else {
        setCouponStatusByPlan((m) => ({ ...m, [planId]: 'invalid' }));
        setCouponMsgByPlan((m) => ({
          ...m,
          [planId]: res.message || 'Invalid coupon',
        }));
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        (e instanceof Error ? e.message : 'Failed to validate coupon');
      setCouponStatusByPlan((m) => ({ ...m, [planId]: 'invalid' }));
      setCouponMsgByPlan((m) => ({ ...m, [planId]: msg }));
    }
  };

  const doCheckout = async (planId: string, withCoupon: boolean) => {
    if (!tenantId) {
      showErrorToast('School context not found. Please sign in again.');
      return;
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const successUrl = `${origin}/admin/billing?checkout=success`;
    const cancelUrl = `${origin}/admin/billing?checkout=cancel`;
    const code = normalizeCoupon(couponByPlan[planId] ?? '');
    const coupon_code =
      withCoupon && code && couponStatusByPlan[planId] === 'applied' ? code : undefined;

    const payload: Parameters<typeof createCheckoutSession>[1] = {
      plan_id: planId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    };
    if (coupon_code) payload.coupon_code = coupon_code;

    setCheckoutLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession(tenantId, payload);
      setCheckoutModalPlanId(null);
      showSuccessToast('Redirecting to checkout…');
      window.location.href = checkout_url;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        (e instanceof Error ? e.message : 'Checkout failed');
      showErrorToast(e);
      if (String(msg).toLowerCase().includes('coupon')) {
        setCouponStatusByPlan((m) => ({ ...m, [planId]: 'invalid' }));
        setCouponMsgByPlan((m) => ({ ...m, [planId]: msg }));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const tenantId =
    (typeof localStorage !== 'undefined' ? localStorage.getItem('X_TENANT_ID') : null) ||
    user?.memberships?.[0]?.school_id ||
    '';

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      setSubLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setSubLoading(true);
    setError(null);

    async function load() {
      try {
        const plansData = await getPublicPlans(tenantId);
        if (!alive) return;
        setPlans(plansData);

        const subscription = await getMyTenantSubscription(tenantId);
        if (!alive) return;
        setSub(subscription);
      } catch (e: unknown) {
        if (!alive) return;
        const message = e instanceof Error ? e.message : 'Failed to load billing data';
        setError(message);
        showErrorToast(e);
      } finally {
        if (!alive) return;
        setLoading(false);
        setSubLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [tenantId]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout && !checkoutMessageShown.current) {
      checkoutMessageShown.current = true;
      if (checkout === 'success') {
        showSuccessToast('Payment successful. Your subscription is now active.');
      } else if (checkout === 'cancel') {
        showErrorToast('Checkout was cancelled.');
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const checkoutPlan = checkoutModalPlanId ? plans.find((p) => p.plan_id === checkoutModalPlanId) : null;

  if (!tenantId && !loading) {
    return (
      <Layout>
        <PageHeader title="Billing & plans" subtitle="Manage your school plan" />
        <Card className="p-6">
          <p className="text-app-text-muted dark:text-gray-400 text-center">
            Unable to load plans. Make sure you are signed in as a school admin and your school is set up.
          </p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title="Billing & plans"
        subtitle="View and subscribe to plans for your school"
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : plans.length === 0 ? (
        <Card className="p-8">
          <div className="text-center py-8 text-app-text-muted dark:text-gray-400">
            No plans available at the moment. Contact support for options.
          </div>
        </Card>
      ) : (
        <>
          {!subLoading && !sub && (
            <div className="mb-4 rounded-lg border border-app-border dark:border-white/10 bg-app-surface-muted/50 dark:bg-white/5 p-3 text-sm text-app-text-muted dark:text-white/80">
              No active subscription found. Choose a plan to subscribe.
            </div>
          )}
          {!subLoading && sub && (
            <div className="mb-4 rounded-lg border border-app-border dark:border-white/10 bg-app-surface-muted/50 dark:bg-white/5 p-3 text-sm text-app-text-muted dark:text-white/80">
              You&apos;re currently on <strong>{sub.entitlements_snapshot?.plan_name ?? 'a plan'}</strong>
              {sub.current_period_end ? (
                <> until <strong>{new Date(sub.current_period_end).toLocaleDateString()}</strong></>
              ) : null}
              .
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {plans.map((plan) => {
              const isCurrentPlan =
                !!sub &&
                sub.status === 'active' &&
                sub.plan_id === plan.plan_id;
              return (
                <PublicPlanCard
                  key={plan.plan_id}
                  plan={plan}
                  isCurrentPlan={isCurrentPlan}
                  onOpenCheckoutModal={setCheckoutModalPlanId}
                />
              );
            })}
          </div>

          {/* Checkout modal: optional coupon then Continue without coupon / Continue to checkout */}
          <Modal
            isOpen={!!checkoutPlan}
            onClose={() => setCheckoutModalPlanId(null)}
            title={checkoutPlan ? `Subscribe to ${checkoutPlan.name}` : 'Checkout'}
            size="md"
          >
            {checkoutPlan && (
              <div className="space-y-4">
                <p className="text-sm text-app-text-muted dark:text-gray-400">
                  {formatPrice(checkoutPlan.amount, checkoutPlan.currency, checkoutPlan.interval)}
                </p>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-app-text dark:text-white">Have a coupon?</p>
                  <div className="flex gap-2">
                    <Input
                      value={couponByPlan[checkoutPlan.plan_id] ?? ''}
                      placeholder="Enter code (e.g. WELCOME50)"
                      onChange={(e) => {
                        const next = normalizeCoupon(e.target.value);
                        setCouponByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: next }));
                        setCouponStatusByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: 'idle' }));
                        setCouponMsgByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: '' }));
                      }}
                      className="flex-1 min-w-0"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleApplyCoupon(checkoutPlan.plan_id)}
                      disabled={
                        !couponByPlan[checkoutPlan.plan_id]?.trim() ||
                        couponStatusByPlan[checkoutPlan.plan_id] === 'checking'
                      }
                    >
                      {couponStatusByPlan[checkoutPlan.plan_id] === 'checking' ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Checking
                        </>
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                  {couponByPlan[checkoutPlan.plan_id]?.trim() &&
                    couponStatusByPlan[checkoutPlan.plan_id] === 'idle' && (
                      <p className="text-xs text-app-text-muted dark:text-gray-400">
                        Click &quot;Apply&quot; to validate the coupon before checkout.
                      </p>
                    )}
                  {couponMsgByPlan[checkoutPlan.plan_id] && (
                    <div
                      className={
                        couponStatusByPlan[checkoutPlan.plan_id] === 'applied'
                          ? 'text-xs text-emerald-600 dark:text-emerald-400'
                          : 'text-xs text-red-600 dark:text-red-400'
                      }
                    >
                      {couponMsgByPlan[checkoutPlan.plan_id]}
                    </div>
                  )}
                  {couponStatusByPlan[checkoutPlan.plan_id] === 'applied' && (
                    <button
                      type="button"
                      className="text-xs text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white underline underline-offset-2"
                      onClick={() => {
                        setCouponByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: '' }));
                        setCouponStatusByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: 'idle' }));
                        setCouponMsgByPlan((m) => ({ ...m, [checkoutPlan.plan_id]: '' }));
                      }}
                    >
                      Clear coupon
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-2 pt-2 border-t border-app-border dark:border-gray-600">
                  <Button
                    onClick={() => doCheckout(checkoutPlan.plan_id, false)}
                    variant="outline"
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? <Spinner size="sm" className="mr-2" /> : null}
                    Continue without coupon
                  </Button>
                  <Button
                    onClick={() => doCheckout(checkoutPlan.plan_id, true)}
                    disabled={
                      checkoutLoading ||
                      (!!couponByPlan[checkoutPlan.plan_id]?.trim() &&
                        couponStatusByPlan[checkoutPlan.plan_id] !== 'applied')
                    }
                  >
                    {checkoutLoading ? <Spinner size="sm" className="mr-2" /> : null}
                    Continue to checkout
                    {couponStatusByPlan[checkoutPlan.plan_id] === 'applied' && ' (with coupon)'}
                  </Button>
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </Layout>
  );
};
